/**
 * Central icon registry for the application
 * Organized for easy maintenance and scalability
 */

import {
  CloseOutline,
  ControlOutline,
  CrownOutline,
  HomeOutline,
  MenuOutline,
  PlusOutline,
  TrophyOutline,
  CompassOutline,
  ScheduleOutline,
  InboxOutline,
  BellOutline,
  NotificationOutline,
  UserAddOutline,
  UserOutline,
  UserDeleteOutline,
  InfoCircleOutline,
  TableOutline,
  AppstoreOutline,
  ClockCircleOutline,
  CalendarOutline,
  ArrowLeftOutline,
  GlobalOutline,
  ArrowRightOutline,
  DeploymentUnitOutline,
  ReloadOutline,
  SearchOutline,
  DesktopOutline,
  MobileOutline,
  SyncOutline,
  FolderOpenOutline,
  FolderAddOutline,
  MessageOutline,
  RightOutline,
  DownOutline,
  UpOutline,
  ArrowUpOutline,
  SwapOutline,
  DeleteOutline,
  CheckOutline,
  TeamOutline,
  BankOutline,
  FileSearchOutline,
  ToolOutline,
  StopOutline,
  UploadOutline,
  DownloadOutline,
  UnorderedListOutline,
  FileOutline,
  FileTextOutline,
  CheckCircleOutline,
  CloseCircleFill,
  CloseCircleOutline,
  ExclamationCircleOutline,
  ExclamationOutline,
  MinusCircleOutline,
  PlusCircleOutline,
  WarningOutline,
  LoadingOutline,
  MailOutline,
  GoogleOutline,
  AppleFill,
  AppleOutline,
  LockOutline,
  LockFill,
  KeyOutline,
  ApartmentOutline,
  LoginOutline,
  LogoutOutline,
  SettingOutline,
  SolutionOutline,
  BarChartOutline,
  ThunderboltOutline,
  RocketOutline,
  EnterOutline,
  FlagOutline,
  FlagFill,
  RightCircleOutline,
  MonitorOutline,
  ProjectOutline,
  ProfileOutline,
  DragOutline,
  FilterOutline,
  MoreOutline,
  CopyOutline,
  EditOutline,
  ExportOutline,
  EyeOutline,
  EyeInvisibleOutline,
  PauseOutline,
  CarryOutOutline,
  SmileOutline,
  CoffeeOutline,
  CheckCircleFill,
  SoundOutline,
  CheckSquareOutline,
  CaretRightOutline,
  PlayCircleOutline,
  TagOutline,
  TagsOutline,
  HolderOutline,
  // Task Type Icons
  FormOutline,
  EnvironmentOutline,
  BorderOuterOutline,
  AimOutline,
  // General Task Icons
  LinkOutline,
  CommentOutline,
  // Map Task Icons
  UndoOutline,
  RotateLeftOutline,
  RotateRightOutline,
  AreaChartOutline,
  // Save Action
  SaveOutline,
  // Cloud & Sync Icons
  CloudOutline,
  CloudSyncOutline,
  CloudUploadOutline,
  CloudDownloadOutline,
  DisconnectOutline,
  // Avatar Customization
  HighlightOutline,
  CameraOutline,
  PictureOutline,
  // Feedback System
  BugOutline,
  StarOutline,
  // Welcome Page
  RiseOutline,
  SendOutline,
  WalletOutline,
  IdcardOutline,
  ArrowDownOutline,
  VerticalAlignBottomOutline,
} from '@ant-design/icons-angular/icons';

/**
 * Returns all icons used throughout the application
 * Centralized for easy management and tree-shaking
 */
export function getNzIcons() {
  return [
    // Authentication & User
    LoginOutline,
    LogoutOutline,
    UserOutline,
    UserAddOutline,
    UserDeleteOutline,
    MailOutline,
    LockOutline,
    LockFill,
    KeyOutline,
    GoogleOutline,
    AppleFill,
    AppleOutline,

    // Navigation & Layout
    HomeOutline,
    MenuOutline,
    ControlOutline,
    SettingOutline,
    SolutionOutline,
    CompassOutline,
    GlobalOutline,

    // Work/Task Management
    InboxOutline,
    ScheduleOutline,
    CalendarOutline,
    TableOutline,
    AppstoreOutline,
    ClockCircleOutline,
    FileSearchOutline,
    BarChartOutline,
    ThunderboltOutline,
    RocketOutline,
    EnterOutline,
    RightCircleOutline,
    ProjectOutline,
    ProfileOutline,
    WalletOutline,
    IdcardOutline,
    DragOutline,
    FilterOutline,
    SoundOutline,
    CheckSquareOutline,

    // Actions & Operations
    PlusOutline,
    SendOutline,
    DeleteOutline,
    CheckOutline,
    CloseOutline,
    UploadOutline,
    DownloadOutline,
    ExportOutline,
    UnorderedListOutline,
    ReloadOutline,
    SearchOutline,
    SyncOutline,
    FlagOutline,
    FlagFill,

    // Navigation Arrows
    ArrowLeftOutline,
    ArrowRightOutline,
    ArrowUpOutline,
    ArrowDownOutline,
    UpOutline,
    DownOutline,
    RightOutline,
    CaretRightOutline,
    VerticalAlignBottomOutline,

    // Organization & Structure
    BankOutline,
    ApartmentOutline,
    DeploymentUnitOutline,
    TeamOutline,
    CrownOutline,
    TrophyOutline,

    // UI Elements
    BellOutline,
    NotificationOutline,
    MessageOutline,
    FolderOpenOutline,
    FolderAddOutline,
    SwapOutline,

    // Status & Indicators
    CheckCircleOutline,
    CloseCircleOutline,
    ExclamationCircleOutline,
    ExclamationOutline,
    MinusCircleOutline,
    PlusCircleOutline,
    WarningOutline,
    LoadingOutline,

    // Device Detection
    DesktopOutline,
    MobileOutline,
    MonitorOutline,

    // File Management
    FileOutline,
    FileTextOutline,
    PictureOutline,
    ToolOutline,
    StopOutline,

    // Additional
    MoreOutline,
    CopyOutline,
    EditOutline,
    InfoCircleOutline,
    EyeOutline,
    EyeInvisibleOutline,
    PauseOutline,
    CarryOutOutline,
    PlayCircleOutline,

    // Maintenance & Recovery
    SmileOutline,
    CoffeeOutline,
    CheckCircleFill,
    CloseCircleFill,

    // Tags
    TagOutline,
    TagsOutline,
    HolderOutline,

    // Task Type Icons
    FormOutline,        // GENERAL task type
    EnvironmentOutline, // GEOPOINT task type
    BorderOuterOutline, // MAP task type
    AimOutline,         // Target/precision indicator

    // General Task Icons
    LinkOutline,        // Links section
    CommentOutline,     // Comments section

    // Map Task Icons
    UndoOutline,        // Undo last action
    RotateLeftOutline,  // Rotate image preview left
    RotateRightOutline, // Rotate image preview right
    AreaChartOutline,   // Area/polygon stats

    // Save Action
    SaveOutline,        // Save button

    // Cloud & Sync Icons
    CloudOutline,       // Cloud/online status
    CloudSyncOutline,   // Cloud sync action
    CloudUploadOutline, // Upload to cloud
    CloudDownloadOutline, // Download from cloud
    DisconnectOutline,  // Offline/disconnected status


    // Feedback System
    BugOutline,         // Bug report icon
    StarOutline,        // Feature request icon

    // Welcome Page
    RiseOutline,        // Momentum/trend indicator
    // Avatar Customization
    HighlightOutline,   // Color selection
    CameraOutline,      // Image/photo upload
  ];
}
